import React, { useState } from "react";
import { Button, message, Modal } from "antd";
import { EyeOutlined, EyeInvisibleOutlined, CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import * as tConst from "../../Common/Constants";
import { AF_TITLE, AF_PROJECT, AF_STATE, AF_ASSIGNED_TO, AF_INITIATIV, AF_START_ITERATION, AF_END_ITERATION, Ticket } from "../../services/Ticket";
import AzureDevOpsService from "../../services/AzureDevOpsService";
import { formatDate } from "../../utils/formatDate";
import AddNew from "./AddNew";

interface ChildrenMenuToolbarProps {
  isLoaded: boolean;
  children: Array<any>;
  selectedTicket: any;
  onClose: () => void;
  onSetRefresh: () => void;
  onPlanningToChildren?: (ticket: any | null) => void; // Added missing prop
  createTicket(project: string, ticketType: string, fields: Record<string, any>, parentUrl : string): Promise<any>
  selectedAreaPath?: string;
  onSelectTicket?: (ticket: any) => void; // Callback to open a child ticket in ChartMenu
  azureDevOpsService: AzureDevOpsService; // Add AzureDevOpsService instance
  showChildren: boolean;
  onToggleChildren: () => void;
}

const ChildrenMenuToolbar: React.FC<ChildrenMenuToolbarProps> = (props) => {
  const [showAddNew, setShowAddNew] = useState(false);

  const handleCopyInitiativeToAll = async () => {
    const parentInitiative = props.selectedTicket.fields[AF_INITIATIV];
    
    if (!parentInitiative) {
      message.warning("Parent has no initiative to copy");
      return;
    }

    if (props.children.length === 0) {
      message.info("No children to update");
      return;
    }

    Modal.confirm({
      title: 'Copy Initiative to All Children',
      icon: <ExclamationCircleOutlined />,
      content: `Copy initiative "${parentInitiative}" to all ${props.children.length} child(ren)? This will overwrite any existing initiatives.`,
      okText: 'Yes, copy',
      cancelText: 'Cancel',
      onOk: async () => {
        let successCount = 0;
        let errorCount = 0;

        for (const child of props.children) {
          try {
            await props.azureDevOpsService.updateTicket(child.id, {
              [AF_INITIATIV]: parentInitiative
            });
            successCount++;
          } catch (error) {
            console.error(`Error updating child ${child.id}:`, error);
            errorCount++;
          }
        }

        if (successCount > 0) {
          message.success(`Updated ${successCount} child(ren)`);
          props.onSetRefresh();
        }
        if (errorCount > 0) {
          message.error(`Failed to update ${errorCount} child(ren)`);
        }
      }
    });
  };

  const handleCopyToChild = async (child: any) => {
    try {
      const fieldsToUpdate: Record<string, any> = {};

      // Copy initiative if child doesn't have one
      const childInitiative = child.fields[AF_INITIATIV];
      const parentInitiative = props.selectedTicket.fields[AF_INITIATIV];
      
      // ".." is Azure DevOps' placeholder for an unset iteration/initiative field
      if ((!childInitiative || childInitiative === ".." || childInitiative === "") && parentInitiative) {
        fieldsToUpdate[AF_INITIATIV] = parentInitiative;
      }

      // Check if child already has start and end iterations
      const childStartIteration = child.fields[AF_START_ITERATION];
      const childEndIteration = child.fields[AF_END_ITERATION];
      const hasExistingIterations = childStartIteration && childEndIteration;

      // Copy start iteration from parent
      const parentStartIteration = props.selectedTicket.fields[AF_START_ITERATION];
      if (parentStartIteration) {
        fieldsToUpdate[AF_START_ITERATION] = parentStartIteration;
      }

      // Copy end iteration from parent
      const parentEndIteration = props.selectedTicket.fields[AF_END_ITERATION];
      if (parentEndIteration) {
        fieldsToUpdate[AF_END_ITERATION] = parentEndIteration;
      }

      if (Object.keys(fieldsToUpdate).length > 0) {
        // If child already has iterations, ask for confirmation
        if (hasExistingIterations) {
          Modal.confirm({
            title: 'Confirm Overwrite',
            icon: <ExclamationCircleOutlined />,
            content: `This child already has start and end iterations. Do you want to overwrite them?`,
            okText: 'Yes, overwrite',
            cancelText: 'Cancel',
            onOk: async () => {
              try {
                await props.azureDevOpsService.updateTicket(child.id, fieldsToUpdate);
                message.success(`Updated child ${child.id}`);
                props.onSetRefresh();
              } catch (error) {
                console.error("Error updating child:", error);
                message.error(`Failed to update child ${child.id}`);
              }
            }
          });
        } else {
          await props.azureDevOpsService.updateTicket(child.id, fieldsToUpdate);
          message.success(`Updated child ${child.id}`);
          props.onSetRefresh();
        }
      } else {
        message.info(`Child ${child.id} already has all values set`);
      }
    } catch (error) {
      console.error("Error updating child:", error);
      message.error(`Failed to update child ${child.id}`);
    }
  };

  if (!props.isLoaded) {
    return <></>;
  } else {
    const numberOfChildren = props.children ? props.children.length : 0;
    return (
      <>
        <div style={{ marginBottom: "10px" }}>
          {numberOfChildren > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: "5px" }}>
              <h3 style={{ margin: 0 }}>Children:</h3>
              <Button
                type="text"
                size="small"
                onClick={props.onToggleChildren}
              >
                {props.showChildren ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                {props.showChildren ? ' Hide' : ' Show'}
              </Button>
              <Button
                type="default"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyInitiativeToAll}
                title="Copy initiative from parent to all children (overwrites existing)"
              >
                Copy Initiative to All
              </Button>
            </div>
          )}
          {props.showChildren && (
            <>
              <Button
                type={showAddNew ? "default" : "primary"}
                size="small"
                onClick={() => {
                  const _toggle = !showAddNew;
                  setShowAddNew(_toggle);
                }}
              >
                New
              </Button>
              <br/>
              {(showAddNew && <AddNew 
                parent={props.selectedTicket} 
                onSetRefresh={() => {}}
                createTicket={props.createTicket}
                onClose={(ticketCreated?: boolean) => {
                  setShowAddNew(false);
                  if (ticketCreated) {
                    props.onSetRefresh();
                  }
                }} 
                teams={tConst.TEAMS_LIST}
                selectedAreaPath={props.selectedAreaPath}
              />)}
            </>
          )}
        </div>

        {numberOfChildren > 0 && props.showChildren && (
          <div style={{ marginTop: "20px" }}>
            <table className="table table-sm table-striped" style={{ border: "1px solid black", borderCollapse: "collapse", width: "100%" }}>
              <thead style={{ backgroundColor: "#f2f2f2" }}>
                <tr>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>ID</th>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>Title</th>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>Project</th>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>Status</th>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>Iteration</th>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>Assigned To</th>
                  <th style={{ border: "1px solid black", padding: "8px", textAlign: "left" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {props.children.map((item: any) => (
                  <tr key={item.id} style={{ backgroundColor: item.fields[AF_STATE] === "Closed" ? "#d9edf7" : "white" }}>
                    <td style={{ border: "1px solid black", padding: "8px" }}>
                      <a
                        href={tConst.AZURE_DEVOPS_BASE_TICKET_URI + item.id}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.id}
                      </a>
                    </td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>
                      {props.onSelectTicket ? (
                        <span
                          onClick={async () => {
                            // Fetch full ticket with children
                            const fullTicket = await props.azureDevOpsService.fetchWorkItemById(item.id);
                            if (fullTicket) {
                              const ticket = new Ticket(fullTicket);
                              ticket.children = [];

                              // Fetch children if they exist
                              if (ticket.childrenIds && ticket.childrenIds.length > 0) {
                                const childrenDetails = await Promise.all(
                                  ticket.childrenIds.map(async (childId: number) => {
                                    try {
                                      const resultChild = await props.azureDevOpsService.fetchWorkItemById(childId);
                                      const childTicket = new Ticket(resultChild, ticket.initiative);
                                      return childTicket;
                                    } catch (err) {
                                      console.error("Error fetching child ticket:", err);
                                      return null;
                                    }
                                  })
                                );
                                ticket.children = childrenDetails.filter((child): child is Ticket => child !== null);
                              }

                              props.onSelectTicket!(ticket);
                            }
                          }}
                          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                        >
                          {item.fields[AF_TITLE]}
                        </span>
                      ) : (
                        <a
                          href={tConst.AZURE_DEVOPS_BASE_TICKET_URI + item.id}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.fields[AF_TITLE]}
                        </a>
                      )}
                    </td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>{item.fields[AF_PROJECT]}</td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>{item.fields[AF_STATE]}</td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>
                      {item.fields[AF_START_ITERATION] && item.fields[AF_END_ITERATION]
                        ? `${formatDate(new Date(item.fields[AF_START_ITERATION]))} - ${formatDate(new Date(item.fields[AF_END_ITERATION]))}`
                        : item.fields[AF_START_ITERATION] 
                          ? formatDate(new Date(item.fields[AF_START_ITERATION]))
                          : item.fields[AF_END_ITERATION] 
                            ? formatDate(new Date(item.fields[AF_END_ITERATION]))
                            : ''}
                    </td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>
                      {item.fields[AF_ASSIGNED_TO]?.displayName || 'Unassigned'}
                    </td>
                    <td style={{ border: "1px solid black", padding: "8px" }}>
                      <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyToChild(item)}
                        title="Add initiative and iterations from parent"
                      >
                        Add
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }
};

export default ChildrenMenuToolbar;